import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { RotateCcw, RefreshCw } from "lucide-react";

const LABELS = { order_register: "Order Register rows", tasks: "Tasks", notes: "Notes" };

export const TestDataResetDialog = ({ user, onClose }) => {
  const [preview, setPreview] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setPreview(null); setPassword("");
    axios.get(`${API}/admin/users/${user.id}/test-data`, { headers: getAuthHeader() })
      .then(r => setPreview(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || "Could not load test data"); onClose(); });
  }, [user, onClose]);

  const reset = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/users/${user.id}/test-data/reset`, { password },
        { headers: getAuthHeader() });
      toast.success(`Wiped ${r.data.total} item(s) for ${user.name}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="test-data-reset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-fuchsia-700">
            <RotateCcw className="w-4 h-4" /> Reset test data — {user?.name}
          </DialogTitle>
          <DialogDescription>
            Permanently deletes everything this test account created. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="py-6 text-center text-sm text-slate-500"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Counting…</div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border divide-y" data-testid="test-data-reset-counts">
              {Object.entries(preview.counts).map(([k, v]) => (
                <div key={k} className="flex justify-between px-3 py-2 text-sm">
                  <span className="text-slate-600">{LABELS[k] || k}</span>
                  <b data-testid={`test-data-count-${k}`}>{v}</b>
                </div>
              ))}
              <div className="flex justify-between px-3 py-2 text-sm bg-slate-50">
                <span className="font-semibold">Total</span>
                <b data-testid="test-data-count-total">{preview.total}</b>
              </div>
            </div>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                   placeholder="Supervisor password" data-testid="test-data-reset-password"
                   onKeyDown={e => e.key === "Enter" && password && preview.total > 0 && reset()} />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="test-data-reset-confirm"
                  disabled={busy || !password || !preview || preview.total === 0} onClick={reset}>
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
            {preview && preview.total === 0 ? "Nothing to wipe" : "Wipe test data"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
